"use client";

import { AnimatedSection } from "@/components/ui/animated-section";
import { useTranslations, useLocale } from 'next-intl';

interface TermsClientProps {
  locale: string;
}

export function TermsClient({ locale }: TermsClientProps) {
  const currentLocale = useLocale();
  const t = useTranslations('terms');

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-20 pb-8 md:pt-24 md:pb-12">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up" delay={0}>
            <div className="text-left">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
                {t('title')}
              </h2>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-4 md:mb-6 max-w-3xl">
                {t('description')}
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-8 md:py-12 pb-16 md:pb-20">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <article className="prose prose-lg max-w-none">
              <AnimatedSection animation="fade-up" delay={0}>
                <p className="text-sm text-muted-foreground mb-6">
                  {t('lastUpdated')}
                </p>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={50}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.service.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.service.content')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={100}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.orderProcess.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.orderProcess.content')}
                  </p>
                  <ol className="list-decimal pl-6 space-y-2 text-base md:text-lg text-muted-foreground">
                    <li>{t('sections.orderProcess.steps.request')}</li>
                    <li>{t('sections.orderProcess.steps.confirmation')}</li>
                    <li>{t('sections.orderProcess.steps.payment')}</li>
                    <li>{t('sections.orderProcess.steps.preparation')}</li>
                    <li>{t('sections.orderProcess.steps.pickup')}</li>
                  </ol>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={150}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.payment.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.payment.content')}
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-base md:text-lg text-muted-foreground">
                    <li>{t('sections.payment.items.advance')}</li>
                    <li>{t('sections.payment.items.remaining')}</li>
                    <li>{t('sections.payment.items.methods')}</li>
                  </ul>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={200}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.orderModifications.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.orderModifications.content')}
                  </p>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.orderModifications.deadline')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={250}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.cancellation.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.cancellation.content')}
                  </p>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.cancellation.refund')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={300}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.allergens.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.allergens.content')}
                  </p>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.allergens.crossContamination')}
                  </p>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.allergens.customerResponsibility')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={350}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.productSafety.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.productSafety.content')}
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-base md:text-lg text-muted-foreground">
                    <li>{t('sections.productSafety.items.nonEdible')}</li>
                    <li>{t('sections.productSafety.items.storage')}</li>
                    <li>{t('sections.productSafety.items.transport')}</li>
                    <li>{t('sections.productSafety.items.freshness')}</li>
                  </ul>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={400}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.cakeCareInstructions.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.cakeCareInstructions.content')}
                  </p>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.cakeCareInstructions.disclaimer')}
                  </p>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.cakeCareInstructions.instructionsInclude')}
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-base md:text-lg text-muted-foreground">
                    <li>{t('sections.cakeCareInstructions.items.temperature')}</li>
                    <li>{t('sections.cakeCareInstructions.items.transport')}</li>
                    <li>{t('sections.cakeCareInstructions.items.handling')}</li>
                    <li>{t('sections.cakeCareInstructions.items.storage')}</li>
                    <li>{t('sections.cakeCareInstructions.items.timing')}</li>
                  </ul>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={450}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.pickupDelivery.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.pickupDelivery.content')}
                  </p>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.pickupDelivery.responsibility')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={500}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.pricingErrors.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.pricingErrors.content')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={550}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.forceMajeure.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.forceMajeure.content')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={600}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.liability.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.liability.content')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={650}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.intellectualProperty.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.intellectualProperty.content')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={700}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.governingLaw.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.governingLaw.content')}
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={750}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.contact.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-4">
                    {t('sections.contact.content')}
                  </p>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    <strong>{t('sections.contact.email')}</strong> littlecakesnl@gmail.com
                  </p>
                </section>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={800}>
                <section className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">{t('sections.changes.title')}</h2>
                  <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                    {t('sections.changes.content')}
                  </p>
                </section>
              </AnimatedSection>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}
